/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
} from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import debounce from "lodash.debounce";
import i18n from "../../i18n";
import { EXPORT_PAGE_SIZE } from "../../constants/common";
import useAuthStore from "../../store/Auth";
import { useGetAllLoginUsersReport } from "../../hooks/report/query";
import { Button } from "../button";
import { ExportLoginUsersReportForExcelPDFBody } from "../../hooks/report/interface";

interface SearchLoginUserReportCardProps {
  setAllUsers: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportLoginUsersReportForExcelPDFBody | undefined>
  >;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const SearchLoginUserReportCardSchema = Yup.object({
  firstName: Yup.string(),
  lastName: Yup.string(),
  username: Yup.string(),
  number: Yup.string().matches(
    /^\d{1,14}$/,
    i18n.language === "en" ? "Enter a valid number." : "أدخل رقماً صحيحاً.",
  ),
  location: Yup.string(),
  building: Yup.string(),
});

const initialState = {
  firstName: "",
  lastName: "",
  username: "",
  number: "",
  location: "",
  building: "",
};

const SearchLoginUserReportCard = ({
  setAllUsers,
  page,
  setPages,
  search,
  setCount,
  setExportBody,
  pageSize,
  setPage,
}: SearchLoginUserReportCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useFormWithSchema(SearchLoginUserReportCardSchema);
  const { t } = useTranslation();

  const user = useAuthStore((state) => state.user);

  const [selectedKey, setSelectedKey] = useState<string>("");

  const { data: allLocations } = useGetAllLocationsDropdown();

  const location = watch("location");
  const building = watch("building");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const username = watch("username");
  const number = watch("number");

  const [debouncedFirstName, setDebouncedFirstName] = useState(firstName);
  const [debouncedLastName, setDebouncedLastName] = useState(lastName);
  const [debouncedUsername, setDebouncedUsername] = useState(username);
  const [debouncedNumber, setDebouncedNumber] = useState(number);

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: location ? location.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings,
          )
        : [],
    [allBuildings],
  );

  const { data: users } = useGetAllLoginUsersReport({
    pageNumber: page,
    pageSize: pageSize,
    firstName: debouncedFirstName,
    lastName: debouncedLastName,
    userName: debouncedUsername,
    mobile: debouncedNumber,
    n_RoleID: user?.n_RoleID,
    user_id: user?.user_id,
    locationID: location ? Number(location) : 0,
    n_BuildingID: building ? Number(building) : 0,
    search: search,
  });

  useEffect(() => {
    setExportBody({
      pageNumber: page,
      pageSize: EXPORT_PAGE_SIZE,
      firstName: debouncedFirstName,
      lastName: debouncedLastName,
      userName: debouncedUsername,
      mobile: debouncedNumber,
      n_RoleID: user?.n_RoleID,
      user_id: user?.user_id,
      locationID: location ? Number(location) : 0,
      n_BuildingID: building ? Number(building) : 0,
      search: search,
    });
  }, [
    building,
    debouncedFirstName,
    debouncedLastName,
    debouncedNumber,
    debouncedUsername,
    location,
    page,
    search,
    setExportBody,
    t,
    user?.n_RoleID,
    user?.user_id,
  ]);

  const handleFirstNameChange = useCallback(
    debounce((value) => setDebouncedFirstName(value), 500),
    [],
  );

  const handleLastNameChange = useCallback(
    debounce((value) => setDebouncedLastName(value), 500),
    [],
  );

  const handleUsernameChange = useCallback(
    debounce((value) => setDebouncedUsername(value), 500),
    [],
  );

  const handleNumberChange = useCallback(
    debounce((value) => setDebouncedNumber(value), 500),
    [],
  );

  useEffect(() => {
    handleFirstNameChange(firstName);
    return () => handleFirstNameChange.cancel();
  }, [firstName]);

  useEffect(() => {
    handleLastNameChange(lastName);
    return () => handleLastNameChange.cancel();
  }, [lastName]);

  useEffect(() => {
    handleUsernameChange(username);
    return () => handleUsernameChange.cancel();
  }, [username]);

  useEffect(() => {
    handleNumberChange(number);
    return () => handleNumberChange.cancel();
  }, [number]);

  useEffect(() => {
    if (users) {
      setAllUsers(users.data || []);
      setPages(users.pagination?.pages || 1);
      setCount(users.pagination?.count || 0);
    }
  }, [setAllUsers, setPages, users, setCount]);

  useEffect(() => {
    setPage(1);
  }, [firstName, lastName, username, number, building, location]);

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations,
      ),
    [allLocations],
  );

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4">
              <Controller
                name="firstName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("firstName")}
                    onChange={(e) => {
                      onChange(e);
                      handleFirstNameChange(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("lastName")}
                    onChange={(e) => {
                      onChange(e);
                      handleLastNameChange(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="username"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("username")}
                    onChange={(e) => {
                      onChange(e);
                      handleUsernameChange(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="number"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("phoneNumber")}
                    onChange={(e) => {
                      onChange(e);
                      handleNumberChange(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterPhoneNumber")}</p>}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchLoginUserReportCard);
